import { DROPDOWN_NAMES } from '@config/constant';
import { DropdownObject } from '.';
import { getDatabase } from '@rxdb/config/databaseConfig';
import { IQueryResponse } from '@services/api';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const DropdownQuery = (): IQueryResponse<Array<DropdownObject>> => {
	const [shouldFetch, setShouldFetch] = useState(true);
	const [isDbChecked, setIsDbChecked] = useState(false);

	const fetchDropdownsData = async (): Promise<Array<DropdownObject>> => {
		const dropdowns: Array<DropdownObject> = DROPDOWN_NAMES.map((dropdown) => ({
			id: dropdown.id,
			name: dropdown.name,
			expiry_date: null,
		}));
		if (dropdowns) {
			const db = await getDatabase();
			const allDropdowns = await db.dropdowns.find().exec();
			const preservedExpiry = new Map<string, string>();
			for (const doc of allDropdowns) {
				const row = doc.toJSON() as DropdownObject;
				if (row.expiry_date) preservedExpiry.set(row.name, row.expiry_date);
			}
			const primaryId = allDropdowns.map((doc: { id: string }) => doc.id);
			await db.dropdowns.bulkRemove(primaryId);
			await db.dropdowns.bulkInsert(
				dropdowns.map((d) => {
					const expiry_date = preservedExpiry.get(d.name);
					return expiry_date ? { ...d, expiry_date } : d;
				})
			);
		}
		return dropdowns;
	};

	const checkDb = async (): Promise<boolean> => {
		const db = await getDatabase();
		const result = await db.dropdowns.find().exec();
		return result.length > 0;
	};

	useEffect(() => {
		const checkDatabase = async (): Promise<void> => {
			const exists = await checkDb();
			setShouldFetch(!exists);
			setIsDbChecked(true);
		};
		checkDatabase();
	}, []);

	const { data, error, isLoading, isPending, isFetching, refetch } = useQuery({
		queryKey: ['dropdowns'],
		queryFn: fetchDropdownsData,
		enabled: isDbChecked && shouldFetch,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		refetch,
		isFetching,
	};
};
